<script lang="ts">
  import { API_URL, VERCEL } from "api";
  import { isAdmin, isCommunityMember, isTeamMember } from "$stores/permissions";
  import { getStores } from "app/stores";
  import { community } from "app/stores/community";
  import { onMount } from "svelte";
  import { browser } from "$app/environment";
  import { alerts } from "app/stores/alert";
  import { fadeSlide } from "app/transitions";
  import { devToolbar } from "app/stores/devToolbar";
  import { portal } from "app/actions/portal.svelte";
  import { sveltekitApiUrl } from "app/utils/urls";
  import Dropdown from "$components/+design-system/Dropdown/Dropdown.svelte";
  import OptionGroup from "$components/+design-system/Dropdown/fields/OptionGroup.svelte";
  import CheckboxOption from "$components/+design-system/Dropdown/fields/CheckboxOption.svelte";
  import Button from "$components/+design-system/Button/Button.svelte";
  import Search from "$components/+design-system/Dropdown/fields/Search.svelte";
  import Separator from "$components/+shared/Separator/Separator.svelte";
  import CSS from "./CSS.svelte";
  import Panel from "./Panel.svelte";
  import api from "$utils/api";
  import Option from "$components/+design-system/Dropdown/fields/Option.svelte";

  const { user } = getStores();
  const hasMemoryStats = browser && (window.performance as any).memory;

  let startTime = performance.now();
  let frame = 0;
  let fps = 0;
  let mem = 0;
  let maxmem = 0;
  let flags: ArrayLike<any> | { [s: string]: any } = [];

  onMount(() => {
    function tick() {
      var time = performance.now();
      frame++;
      if (time - startTime > 1000) {
        fps = Math.round(frame / ((time - startTime) / 1000));
        startTime = time;
        frame = 0;
        const perf = window.performance as any;
        if (perf.memory) {
          mem = perf.memory.usedJSHeapSize / 1048576;
          maxmem = perf.memory.jsHeapSizeLimit / 1048576;
        }
      }
      window.requestAnimationFrame(tick);
    }
    tick();
    fetch(`${sveltekitApiUrl()}/flags`)
      .then((response) => response.json())
      .then((data) => {
        flags = data.flags;
        console.log(data);
      });
  });
  $: selectedFlags = Object.entries(flags)
    .map(([flag, info]) => ({ flag, info }))
    .filter((option) => option.info.is_active)
    .map((option) => option.flag);

  $: communityFlags = $community.flags.map((flag) => flag.name);
</script>

<!-- A status bar with dev user info -->
<div
  transition:fadeSlide={{ duration: 100 }}
  use:portal
  class="scrollbar-thin top-shadow fixed inset-x-0 bottom-0
       flex items-center justify-between gap-16 overflow-x-auto border-t
       border-black/25 bg-white/90 px-4 py-1 font-mono text-xs font-medium
       text-neutral-500 brightness-105 backdrop-blur-lg dark:border-white/20
      dark:bg-neutral-900/75 dark:text-neutral-400 [&>*]:shrink-0 [&_button]:!text-xs"
>
  <span>
    {VERCEL.vercel_env == "staging"
      ? "staging"
      : VERCEL.vercel_env == "preview"
        ? VERCEL.pr
        : "local"}
  </span>
  <Dropdown>
    <Button
      slot="trigger"
      size="small"
      let:active
      variant={active ? "active" : "secondary"}
      label={"Community Feature Flags"}
    />
    <OptionGroup>
      {#each communityFlags as communityFlag}
        <Option title={communityFlag} />
      {/each}
    </OptionGroup>
  </Dropdown>
  <Dropdown>
    <Button
      slot="trigger"
      size="small"
      let:active
      variant={active ? "active" : "secondary"}
      label="Feature Flags"
      tooltipLabel={selectedFlags.join() || "None"}
    />
    <Search
      placeholder="Filter flags..."
      on:enter={(e) => console.log(`user wants to do something with`, e.detail)}
    />
    <Separator class="w-full" />
    <OptionGroup bind:values={selectedFlags} allowEmpty>
      {#each Object.entries(flags) as [flag]}
        <CheckboxOption
          title={flag}
          value={flag}
          on:click={() => api.apiUsersToggleFlag({ flagToggleSerialiser: { name: flag } })}
        />
      {/each}
    </OptionGroup>
  </Dropdown>

  <div class="flex items-center [&_button]:!text-neutral-900 [&_button]:dark:!text-neutral-300">
    API:
    <Button
      size="small"
      variant="tertiary"
      label={API_URL}
      on:click={() => {
        navigator.clipboard.writeText(API_URL);
        alerts.success("API URL copied to clipboard");
      }}
    />
  </div>

  <div class="flex items-center gap-2">
    <span>FPS: <span class="text-neutral-700 dark:text-neutral-300">{fps}</span></span>
    <div class="flex h-[31px] gap-2">
      <Panel name="FPS" fg="#737373" bg="#262626" value={fps} maxValue={120} />
      {#if hasMemoryStats}
        <Panel name="MEM" fg="#737373" bg="#262626" value={mem} maxValue={maxmem} />
      {/if}
    </div>
  </div>
  <div class="flex items-center gap-2">
    <span>User:</span>
    <span class="font-medium text-neutral-700 dark:text-neutral-300"
      >{$user.isAuthed ? $user.handle : "Unauthed"}</span
    >
    <!-- <input
        class="bg-black"
        type="text"
        bind:value={hijackUUID}
        on:change={async (e) => {
          session.hijack(hijackUUID);
        }}
      /> -->
  </div>

  <div class="flex items-center gap-2">
    <span>Workspace</span>
    <span class="font-medium text-neutral-700 dark:text-neutral-300">{$community.name}</span>
    <span class="flex">
      ({#if $isAdmin}
        <span title="Admin">A</span>
      {/if}
      {#if $isTeamMember}<span title="Team member">T</span>{/if}
      {#if $isCommunityMember}<span title="Member">M</span>{/if})
    </span>
  </div>

  {#if browser}
    <CSS />
  {/if}

  <Button variant="tertiary" size="small" on:click={() => ($devToolbar = false)} label="Hide (`)">
    Hide (`)
  </Button>
</div>
